// Per-part diagrammatic graphic — picks the right visualization based on
// part.type and surfaces it on the Part detail page's Graphics tab. v1 only
// had this for collars (nesting SVG); v2 extends the pattern so every type
// gets a quick visual that an estimator or operator can glance at.
//
// Strategy by type:
//   - machined         → bar/block silhouette with overall dims pulled from
//                        materialCost (perBar/perSheet) or notes; stub when
//                        nothing's known.
//   - cut_blank/hybrid → flat rectangle showing the outer profile + a caption
//                        for material + thickness when present.
//   - weldment         → stacked sub-part chips (label + tiny box per sub).
//   - collar           → reuse the same nested-tile SVG the CollarNestingStep
//                        renders — extracted here at a smaller scale.
//   - purchased        → vendor logo placeholder + SKU label.
//   - turninsulation   → cheeky 3-arc concentric coil illustration.
//   - fallback         → gray "(no graphic for this part type yet)" box.
//
// All SVG content is inline — fine for a presentational graphic. The wrapper
// uses Tailwind; only the SVG attrs / per-tile styling stays inline.

import type { Part } from '@/shared/crmTypes';
import { calcCollarNesting, COLLAR_DEFAULTS } from '@/shared/collarMath';

export interface PartGraphicProps {
  part: Part;
  /** Pixel width budget. Component scales to fit. Default 360. */
  width?: number;
}

// Accent — borrowed from CollarNestingStep so collar visuals match.
const ACCENT = '#0891B2';

export function PartGraphic({ part, width = 360 }: PartGraphicProps) {
  switch (part.type) {
    case 'machined':
      return <MachinedGraphic part={part} width={width} />;
    case 'cut_blank':
    case 'hybrid':
      return <SheetRectGraphic part={part} width={width} />;
    case 'weldment':
      return <WeldmentGraphic part={part} width={width} />;
    case 'collar':
      return <CollarGraphic part={part} width={width} />;
    case 'purchased':
      return <PurchasedGraphic part={part} width={width} />;
    case 'turninsulation':
      return <TurnInsulationGraphic width={width} />;
    default:
      return <FallbackGraphic width={width} />;
  }
}

// ─────────────────────────────────────────────────────────────
// Wrappers
// ─────────────────────────────────────────────────────────────

function GraphicFrame({
  width,
  caption,
  children,
}: {
  width: number;
  caption?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="bg-paper border border-line rounded p-3 inline-block max-w-full"
      style={{ width }}
    >
      <div className="flex items-center justify-center overflow-hidden">{children}</div>
      {caption && (
        <div className="text-[11px] text-text-mute text-center mt-2 italic">{caption}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Machined — bar/block silhouette
// ─────────────────────────────────────────────────────────────

function MachinedGraphic({ part, width }: { part: Part; width: number }) {
  // No real geometry in the schema yet — show a stylized bar with the
  // part number as the centerline label and pull whatever cost-side hints
  // we have ("from $X/bar") into the caption.
  const hasMaterial = !!part.materialCost;
  const perBar = part.materialCost?.perBar;
  const perSheet = part.materialCost?.perSheet;

  const svgW = width - 24; // padding budget for the frame
  const svgH = 110;
  const barW = svgW - 30;
  const barH = 38;
  const barX = 15;
  const barY = (svgH - barH) / 2;

  const caption = hasMaterial
    ? perBar !== undefined
      ? `Bar stock · $${perBar.toFixed(2)} / bar`
      : perSheet !== undefined
        ? `Sheet stock · $${perSheet.toFixed(2)} / sheet`
        : 'Material spec captured'
    : '(no dimensions captured)';

  return (
    <GraphicFrame width={width} caption={caption}>
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        {/* Block silhouette with a subtle 3D shoulder for depth cue */}
        <rect
          x={barX + 4}
          y={barY + 4}
          width={barW}
          height={barH}
          fill="#1F2937"
          opacity={0.15}
        />
        <rect
          x={barX}
          y={barY}
          width={barW}
          height={barH}
          fill="#9CA3AF"
          stroke="#374151"
          strokeWidth={1.5}
        />
        {/* End-grain hint on the right face */}
        <line
          x1={barX + barW - 8}
          y1={barY + 4}
          x2={barX + barW - 8}
          y2={barY + barH - 4}
          stroke="#374151"
          strokeWidth={1}
          strokeDasharray="2 2"
        />
        {/* Dimension callout — top edge */}
        <line
          x1={barX}
          y1={barY - 10}
          x2={barX + barW}
          y2={barY - 10}
          stroke="#6B7280"
          strokeWidth={0.8}
          markerStart="url(#m_arrow)"
          markerEnd="url(#m_arrow)"
        />
        <text
          x={svgW / 2}
          y={barY - 14}
          fontSize="10"
          fontFamily="monospace"
          fill="#374151"
          textAnchor="middle"
        >
          {part.number}
        </text>
        {/* Arrowhead marker */}
        <defs>
          <marker
            id="m_arrow"
            markerWidth="6"
            markerHeight="6"
            refX="3"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L6,3 L0,6 z" fill="#6B7280" />
          </marker>
        </defs>
      </svg>
    </GraphicFrame>
  );
}

// ─────────────────────────────────────────────────────────────
// Cut-blank / hybrid — flat rect with material caption
// ─────────────────────────────────────────────────────────────

function SheetRectGraphic({ part, width }: { part: Part; width: number }) {
  const svgW = width - 24;
  const svgH = 130;
  // Pad inward so the rect breathes within the frame.
  const rectX = 18;
  const rectY = 18;
  const rectW = svgW - 36;
  const rectH = svgH - 36;

  // Try to read a thickness hint from the first DXF / notes (cheap regex
  // since we don't carry a discrete thickness field yet).
  const firstDxf = part.dxfFiles?.[0];
  const thicknessMatch = part.notes?.match(/(\d+\/\d+|\d+(\.\d+)?)\s*(in|"|ga)/i);
  const materialHint = thicknessMatch ? thicknessMatch[0] : firstDxf?.vendor;
  const caption = materialHint
    ? `Cut blank · ${materialHint}`
    : part.dxfFiles && part.dxfFiles.length > 0
      ? `DXF: ${part.dxfFiles[0].filename}`
      : 'Cut blank';

  return (
    <GraphicFrame width={width} caption={caption}>
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        {/* Drop shadow */}
        <rect
          x={rectX + 3}
          y={rectY + 3}
          width={rectW}
          height={rectH}
          fill="#1F2937"
          opacity={0.12}
        />
        {/* Sheet rectangle */}
        <rect
          x={rectX}
          y={rectY}
          width={rectW}
          height={rectH}
          fill="#E5E7EB"
          stroke="#374151"
          strokeWidth={1.5}
        />
        {/* Corner cut-line accent (laser/plasma flavor) */}
        <circle cx={rectX} cy={rectY} r={3} fill={ACCENT} />
        <text
          x={rectX + rectW / 2}
          y={rectY + rectH / 2 + 4}
          fontSize="11"
          fontFamily="monospace"
          fill="#374151"
          textAnchor="middle"
        >
          {part.number}
        </text>
      </svg>
    </GraphicFrame>
  );
}

// ─────────────────────────────────────────────────────────────
// Weldment — stacked sub-part chips
// ─────────────────────────────────────────────────────────────

function WeldmentGraphic({ part, width }: { part: Part; width: number }) {
  const subs = part.weldment?.subParts ?? [];
  const weldOps = part.weldment?.weldOps ?? [];

  if (subs.length === 0) {
    return (
      <GraphicFrame width={width} caption="No sub-parts captured yet">
        <div className="py-10 text-xs text-text-mute italic">empty weldment</div>
      </GraphicFrame>
    );
  }

  // Render each sub-part as a stacked horizontal bar with its number on the
  // left and its description trailing right. A small weld-bead glyph between
  // adjacent bars hints at the joint.
  const svgW = width - 24;
  const rowH = 30;
  const gap = 14; // bead gutter between rows
  const svgH = subs.length * rowH + (subs.length - 1) * gap + 20;

  return (
    <GraphicFrame
      width={width}
      caption={`${subs.length} sub-part${subs.length === 1 ? '' : 's'} · ${weldOps.length} weld op${weldOps.length === 1 ? '' : 's'}`}
    >
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        {subs.map((sp, i) => {
          const y = 10 + i * (rowH + gap);
          return (
            <g key={sp.id || `${sp.number}-${i}`}>
              <rect
                x={10}
                y={y}
                width={svgW - 20}
                height={rowH}
                fill="#FEF3C7"
                stroke="#B45309"
                strokeWidth={1.3}
                rx={2}
              />
              <text
                x={18}
                y={y + rowH / 2 + 4}
                fontSize="11"
                fontFamily="monospace"
                fontWeight="bold"
                fill="#92400E"
              >
                {sp.number}
              </text>
              {sp.description && (
                <text
                  x={svgW - 14}
                  y={y + rowH / 2 + 4}
                  fontSize="10"
                  fill="#78350F"
                  textAnchor="end"
                >
                  {sp.description.slice(0, 24)}
                  {sp.description.length > 24 ? '…' : ''}
                </text>
              )}
              {/* Bead between rows (skip after the last sub-part) */}
              {i < subs.length - 1 && (
                <g>
                  {[0, 1, 2, 3].map((dot) => (
                    <circle
                      key={dot}
                      cx={svgW / 2 - 18 + dot * 12}
                      cy={y + rowH + gap / 2}
                      r={2.5}
                      fill="#F59E0B"
                    />
                  ))}
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </GraphicFrame>
  );
}

// ─────────────────────────────────────────────────────────────
// Collar — reuse the same nest SVG from CollarNestingStep
// ─────────────────────────────────────────────────────────────

function CollarGraphic({ part, width }: { part: Part; width: number }) {
  const collar = part.collar;
  if (!collar) {
    return (
      <GraphicFrame width={width} caption="Sheet geometry not set">
        <div className="py-10 text-xs text-text-mute italic">no sheet spec</div>
      </GraphicFrame>
    );
  }

  const outsideLen = collar.outsideLen ?? 30;
  const outsideWid = collar.outsideWid ?? 10;
  const frameWidth = collar.frameWidth ?? 2;
  const sheetLen = collar.sheetLen ?? COLLAR_DEFAULTS.sheetLen;
  const sheetWid = collar.sheetWid ?? COLLAR_DEFAULTS.sheetWid;
  const spacing = collar.spacing ?? COLLAR_DEFAULTS.spacing;

  const nest = calcCollarNesting(outsideLen, outsideWid, sheetLen, sheetWid, spacing);
  if (nest.count === 0) {
    return (
      <GraphicFrame width={width} caption="Doesn't fit on selected sheet">
        <div className="py-10 text-xs text-orange italic">no valid nest</div>
      </GraphicFrame>
    );
  }

  return (
    <GraphicFrame
      width={width}
      caption={`${nest.count} parts / sheet · ${nest.cols}×${nest.rows}${nest.rotated ? ' · rotated' : ''}`}
    >
      <CollarNestSvg
        cols={nest.cols}
        rows={nest.rows}
        partLen={nest.partLen}
        partWid={nest.partWid}
        sheetLen={sheetLen}
        sheetWid={sheetWid}
        frameWidth={frameWidth}
        maxWidth={width - 28}
      />
    </GraphicFrame>
  );
}

// Local copy of CollarNestingStep's NestingSvg, sized down for the detail
// page. Kept self-contained per the spec — don't touch CollarNestingStep.
interface CollarNestSvgProps {
  cols: number;
  rows: number;
  partLen: number;
  partWid: number;
  sheetLen: number;
  sheetWid: number;
  frameWidth: number;
  maxWidth: number;
}

function CollarNestSvg({
  cols,
  rows,
  partLen,
  partWid,
  sheetLen,
  sheetWid,
  frameWidth,
  maxWidth,
}: CollarNestSvgProps) {
  const maxHeight = Math.round(maxWidth / 2); // ~2:1 sheet aspect default
  const scale = Math.min(maxWidth / sheetLen, maxHeight / sheetWid);
  const svgW = sheetLen * scale;
  const svgH = sheetWid * scale;
  const fw = Math.max(0, frameWidth) * scale;

  const tiles: React.ReactNode[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = c * partLen * scale;
      const y = r * partWid * scale;
      const w = partLen * scale;
      const h = partWid * scale;
      const showInner = fw > 0 && w - 2 * fw > 3 && h - 2 * fw > 3;
      tiles.push(
        <g key={`${r}-${c}`}>
          <rect
            x={x + 0.5}
            y={y + 0.5}
            width={w - 1}
            height={h - 1}
            fill={ACCENT}
            fillOpacity={0.55}
            stroke={ACCENT}
            strokeWidth={1}
          />
          {showInner && (
            <rect
              x={x + fw}
              y={y + fw}
              width={w - 2 * fw}
              height={h - 2 * fw}
              fill="#FFFFFF"
              stroke={ACCENT}
              strokeWidth={0.5}
              strokeDasharray="2 2"
            />
          )}
        </g>,
      );
    }
  }

  return (
    <svg
      width={svgW}
      height={svgH}
      viewBox={`0 0 ${svgW} ${svgH}`}
      className="border border-line-dark rounded max-w-full"
      style={{
        background:
          'repeating-linear-gradient(45deg, #FAFAFA 0, #FAFAFA 6px, #F0F0F0 6px, #F0F0F0 8px)',
      }}
    >
      {tiles}
      <rect
        x={0.5}
        y={0.5}
        width={svgW - 1}
        height={svgH - 1}
        fill="none"
        stroke="#1F2937"
        strokeWidth={1.2}
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Purchased — vendor box + SKU
// ─────────────────────────────────────────────────────────────

function PurchasedGraphic({ part, width }: { part: Part; width: number }) {
  const source = part.purchasedSource;
  const vendor = source?.vendor ?? 'other';
  const sku = source?.sku ?? '(no SKU)';
  const markup =
    source?.markupPct !== undefined ? `${source.markupPct.toFixed(0)}% markup` : 'no markup set';

  return (
    <GraphicFrame width={width} caption={`Vendor: ${vendor} · ${markup}`}>
      <div className="flex flex-col items-center gap-2 py-3">
        {/* Vendor "logo" placeholder — solid panel with the name typed on it. */}
        <div className="w-32 h-20 bg-bg border border-line-dark rounded flex items-center justify-center">
          <span className="text-base font-bold font-mono text-text-mute uppercase tracking-wide">
            {vendor.slice(0, 8)}
          </span>
        </div>
        <div className="text-xs font-mono text-text">{sku}</div>
      </div>
    </GraphicFrame>
  );
}

// ─────────────────────────────────────────────────────────────
// Turn insulation — 3 concentric arcs (cheeky coil glyph)
// ─────────────────────────────────────────────────────────────

function TurnInsulationGraphic({ width }: { width: number }) {
  const svgW = width - 24;
  const svgH = 130;
  const cx = svgW / 2;
  const cy = svgH / 2;

  // Three concentric half-arcs at different radii — coil + tape wraps glyph.
  return (
    <GraphicFrame width={width} caption="Turn insulation · B-stage tape (calculator coming)">
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        {/* Innermost coil wire */}
        <circle cx={cx} cy={cy} r={18} fill="none" stroke="#B45309" strokeWidth={4} />
        {/* Tape wrap 1 */}
        <path
          d={`M ${cx - 32} ${cy} A 32 18 0 0 1 ${cx + 32} ${cy}`}
          fill="none"
          stroke="#D97706"
          strokeWidth={3}
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - 32} ${cy} A 32 18 0 0 0 ${cx + 32} ${cy}`}
          fill="none"
          stroke="#D97706"
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray="4 3"
        />
        {/* Tape wrap 2 (wider) */}
        <path
          d={`M ${cx - 48} ${cy + 2} A 48 26 0 0 1 ${cx + 48} ${cy + 2}`}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - 48} ${cy + 2} A 48 26 0 0 0 ${cx + 48} ${cy + 2}`}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeDasharray="3 3"
        />
        {/* Outer glow */}
        <ellipse
          cx={cx}
          cy={cy + 4}
          rx={60}
          ry={32}
          fill="none"
          stroke="#FEF3C7"
          strokeWidth={6}
          opacity={0.6}
        />
      </svg>
    </GraphicFrame>
  );
}

// ─────────────────────────────────────────────────────────────
// Fallback
// ─────────────────────────────────────────────────────────────

function FallbackGraphic({ width }: { width: number }) {
  return (
    <GraphicFrame width={width}>
      <div className="bg-bg border border-dashed border-line-dark rounded w-full h-28 flex items-center justify-center text-xs text-text-mute italic">
        (no graphic for this part type yet)
      </div>
    </GraphicFrame>
  );
}
